import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { AlertTitle, Box, Button, Stack, TextField, Typography, styled } from "~/common/mui";
import { type ReactNode, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";

import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { ActionIconButton, ActionIconButtonType } from "~/common/components/Button/ActionIconButton";
import EditIcon from "@mui/icons-material/Edit";
import { Controller, type FieldErrors, useForm } from "react-hook-form";
import {
  AutosuggestProductInput,
  useFetchProductAutosuggest,
} from "~/common/components/AutosuggestProductInput/AutosuggestProductInput";
import { Trans, useTranslation } from "react-i18next";
import { pnaAvailabilityQueryOptions } from "~/domains/pna/pnaQueries";
import { toAvailabilityOutcomeFromQuery } from "~/domains/pna/pnaSnapshot";
import type { B2BUnitDataDTO } from "~/features/cart/cart/dto/CartResponseDTO";
import Modal from "~/common/components/Modal";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { LinkButton } from "~/common/components/Link";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { CONTRACT_OVERVIEW_PAGE } from "~/utils/url/staticUrls";
import { isDateInvalidForDelivery } from "~/common/utils/dateUtils";
import { Alert } from "~/common/mui/Alert/Alert";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { MUIDateInput } from "~/common/components/MUIDateInput";
import { createContract } from "~/features/contracts/api/createContract";

const TilesGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "repeat(1, minmax(0, 1fr))",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: theme.spacing(3),
}));

const ActionsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(3),
  gridTemplateColumns: "repeat(4, 1fr)",
  gridTemplateAreas: '"clear - - submit"',
}));

const AddressInfoButton = styled(LinkButton, {
  shouldForwardProp: (prop) => prop !== "$selected",
})<{ $selected?: boolean }>(({ theme, $selected }) => ({
  alignItems: "flex-start !important",
  color: `${$selected ? theme.palette.primary.main : theme.palette.text.primary} !important`,
  lineHeight: "18px",
}));

const ModalAddressButton = styled(AddressInfoButton)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(5),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

interface FormValues {
  contractNumber: string;
  startDate: Date | null;
  endDate: Date | null;
  totalQuantity: number | null;
  numberOfDeliveries: number | null;
  productId: string;
  packagingOption: number | null;
  unloadingPoint: string | null;
  comment: string;
  shippingAddressParentUnit: string;
  shippingAddressId: string;
  shippingCompany: string;
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingAddressPostalCode: string;
  shippingAddressProvince: string;
  shippingAddressCountry: string;
}

enum ErrorType {
  QUANTITY = "QUANTITY_ERROR",
  DATE = "DATE_ERROR",
}

const Tile = ({ title, children }: { title: string; children: ReactNode }) => {
  return (
    <Stack
      sx={(theme) => ({ border: 1, borderColor: "divider", borderRadius: theme.shape.borderRadius, p: 2 })}
      gap={2}
    >
      <Typography variant="titleMedium" component="h3" sx={{ m: 0 }}>
        {title}
      </Typography>
      {children}
    </Stack>
  );
};

const DynamicErrorMessage = ({ errors }: { errors: FieldErrors<FormValues> }) => {
  const { t } = useTranslation();
  let title;
  let message;
  if (Object.keys(errors).length === 0) {
    return null;
  } else if (errors.packagingOption?.message === ErrorType.QUANTITY) {
    title = t("newContract.quantityErrorTitle");
    message = t("newContract.quantityErrorMessage");
  } else if (errors.startDate?.message === ErrorType.DATE || errors.endDate?.message === ErrorType.DATE) {
    title = t("newContract.dateErrorTitle");
    message = t("newContract.dateErrorMessage");
  } else {
    message = t("newContract.missingFieldsErrorMessage");
  }
  return (
    <Alert severity={"error"}>
      {title && <AlertTitle>{title}</AlertTitle>}
      {message}
    </Alert>
  );
};

const RequestContractPage = ({ addresses, soldTo }: Props) => {
  const parsedUnloadingPoints = useMemo(() => {
    return (
      soldTo.children &&
      soldTo.children.flatMap((child) => {
        if (child.unloadingPoints) {
          return child.unloadingPoints.map((point) => ({
            uid: child.uid,
            id: point.id,
            name: point.name,
          }));
        } else {
          return [];
        }
      })
    );
  }, [soldTo]);

  const DEFAULT_ADDRESS = addresses.find((address) => address.defaultAddress);

  const EMPTY_VALUES = {
    contractNumber: "",
    startDate: null,
    endDate: null,
    totalQuantity: null,
    numberOfDeliveries: null,
    productId: "",
    packagingOption: null,
    unloadingPoint: null,
    comment: "",
    shippingAddressParentUnit: DEFAULT_ADDRESS?.parentUnit ?? "",
    shippingAddressId: DEFAULT_ADDRESS?.id ?? "",
    shippingCompany: DEFAULT_ADDRESS?.companyName ?? "",
    shippingAddressLine1: DEFAULT_ADDRESS?.line1 ?? "",
    shippingAddressLine2: DEFAULT_ADDRESS?.line2 ?? "",
    shippingAddressPostalCode: DEFAULT_ADDRESS?.postalCode ?? "",
    shippingAddressProvince: DEFAULT_ADDRESS?.province ?? "",
    shippingAddressCountry: DEFAULT_ADDRESS?.country.name ?? "",
  };

  const { t } = useTranslation();
  const { register, watch, setValue, handleSubmit, control, getValues, formState, reset, trigger } =
    useForm<FormValues>({
      defaultValues: EMPTY_VALUES,
      mode: "all",
    });

  const { inputProps, products, selectedProduct, packCode } = useFetchProductAutosuggest(watch("productId") ?? "");
  const selectedProductCode = selectedProduct?.code;
  const { val: addressModal, setTrue: showAddressModal, setFalse: hideAddressModal } = useBooleanState(false);

  const availabilityOptions = pnaAvailabilityQueryOptions({
    productCode: selectedProductCode ?? "",
    enabled: Boolean(selectedProductCode),
  });
  const availabilityQuery = useQuery(availabilityOptions);
  const availabilityOutcome = toAvailabilityOutcomeFromQuery(availabilityQuery, availabilityOptions.enabled === true);
  const packageOptions = availabilityOutcome.status === "success" ? availabilityOutcome.data : undefined;

  const submitHandler = handleSubmit((values) => {
    const formattedData = {
      contractNumber: values.contractNumber,
      quantity: values.totalQuantity,
      numberOfDeliveries: values.numberOfDeliveries,
      packageCode: packageOptions?.packagingInformation.find((entry) => entry.packSize === values.packagingOption)
        ?.packCode,
      startDate: values.startDate,
      endDate: values.endDate,
      materialNumber: selectedProduct?.sapMaterialNumber,
      address: values.shippingAddressId,
      unloadingPoint: values.unloadingPoint,
      comment: values.comment,
    };
    createContract(formattedData).then(() => redirectToUrl(`${CONTRACT_OVERVIEW_PAGE}?success`));
  });

  const changeSelectedAddress = (address: AddressDTO) => {
    setValue("shippingAddressParentUnit", address.parentUnit ?? "");
    setValue("shippingAddressId", address.id);
    setValue("shippingCompany", address.companyName);
    setValue("shippingAddressLine1", address.line1!);
    setValue("shippingAddressLine2", address.line2!);
    setValue("shippingAddressPostalCode", address.postalCode);
    setValue("shippingAddressProvince", address.province);
    setValue("shippingAddressCountry", address.country.name);
    if (address.id === watch("shippingAddressId")) {
      setValue("unloadingPoint", null);
    }
  };

  const filteredUnloadingPoints = parsedUnloadingPoints?.filter(
    (point) => point.uid === watch("shippingAddressParentUnit"),
  );

  const AddressInformation = ({
    id,
    company,
    line1,
    line2,
    postalCode,
    province,
    country,
    onClick,
  }: {
    id: string;
    company: string;
    line1: string;
    line2: string;
    postalCode: string;
    province: string;
    country: string;
    onClick?: () => void;
  }) => {
    const isSelected = watch("shippingAddressId") === id;
    return (
      <ModalAddressButton onClick={onClick} $selected={isSelected}>
        <div className="d-flex align-items-center">
          <span className="f-bold">{company ?? ""}</span>
          {isSelected && <CheckCircleOutlineIcon sx={{ color: "primary.main", pl: 1, fontSize: 16 }} />}
        </div>
        <span>{`${line1 ?? ""} ${line2 ?? ""}`}</span>
        <span>{`${postalCode ?? ""} ${province ?? ""}`}</span>
        <span>{country && <Trans i18nKey={country} />}</span>
      </ModalAddressButton>
    );
  };

  const checkInvalidDates = () => {
    const startDate = getValues("startDate");
    const endDate = getValues("endDate");
    if (startDate && endDate) {
      return startDate < endDate || ErrorType.DATE;
    }
  };

  const checkInvalidQuantity = () => {
    if (!selectedProduct) {
      return true;
    }
    return watch("totalQuantity")! % watch("packagingOption")! === 0 || ErrorType.QUANTITY;
  };

  return (
    <form onSubmit={submitHandler}>
      {addressModal && (
        <Modal title={t("newContract.selectShippingAddress")} onClose={hideAddressModal}>
          <Stack direction="column">
            <Typography sx={(theme) => ({ p: theme.spacing(0, 3, 2), color: "text.secondary" })}>
              {t("newContract.defaultShippingAddress")}
            </Typography>
            {DEFAULT_ADDRESS && (
              <AddressInformation
                onClick={() => {
                  changeSelectedAddress(DEFAULT_ADDRESS);
                  hideAddressModal();
                }}
                id={DEFAULT_ADDRESS?.id}
                company={DEFAULT_ADDRESS?.companyName}
                line1={DEFAULT_ADDRESS.line1 ?? ""}
                line2={DEFAULT_ADDRESS.line2 ?? ""}
                postalCode={DEFAULT_ADDRESS?.postalCode}
                province={DEFAULT_ADDRESS?.province}
                country={DEFAULT_ADDRESS?.country.name}
              />
            )}
            <Typography className="pt-2" sx={(theme) => ({ p: theme.spacing(0, 3, 2), color: "text.secondary" })}>
              {t("newContract.accountApprovedAddresses")}
            </Typography>
            {addresses
              .filter((address) => !address.defaultAddress)
              .map((address) => (
                <AddressInformation
                  onClick={() => {
                    changeSelectedAddress(address);
                    hideAddressModal();
                  }}
                  id={address?.id}
                  company={address?.companyName}
                  line1={address.line1 ?? ""}
                  line2={address.line2 ?? ""}
                  postalCode={address?.postalCode}
                  province={address?.province}
                  country={address?.country.name}
                />
              ))}
          </Stack>
        </Modal>
      )}
      <Stack direction="column" gap={2}>
        <PageTitle>{t("newContract.title")}</PageTitle>
        <TilesGrid>
          <Tile title={t("newContract.contractTile")}>
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  error={!!error}
                  value={value}
                  onChange={onChange}
                  label={t("contractOverview.table.contractNo") + "*"}
                  InputProps={{
                    startAdornment: error && <ErrorOutlineIcon sx={{ color: "error.main", pr: 1, fontSize: 16 }} />,
                  }}
                  placeholder={error ? t("newContract.contractErrorPlaceholder") : undefined}
                  inputProps={{
                    "clta-new-contract-page-element-marker": "contract-id-input",
                  }}
                />
              )}
              name="contractNumber"
              control={control}
              rules={{ required: true }}
            />

            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <MUIDateInput
                  date={value}
                  onChange={(newDate) => {
                    onChange(newDate);
                    trigger(["startDate", "endDate"]);
                  }}
                  minDate={new Date(new Date().getFullYear(), 0, 1)}
                  label={t("newContract.startDate") + "*"}
                  helperLabel={
                    <Typography sx={error ? { color: "error.main" } : undefined}>
                      {t("bulkUpload.help.example.dateFormat")}
                    </Typography>
                  }
                  isInvalid={!!error}
                  isDayDisabled={isDateInvalidForDelivery}
                  startAdornment={error && <ErrorOutlineIcon fontSize="small" sx={{ color: "error.main" }} />}
                />
              )}
              name="startDate"
              control={control}
              rules={{
                required: true,
                validate: checkInvalidDates,
              }}
            />
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <MUIDateInput
                  date={value}
                  onChange={(newDate) => {
                    onChange(newDate);
                    trigger(["startDate", "endDate"]);
                  }}
                  label={t("newContract.endDate") + "*"}
                  helperLabel={
                    <Typography sx={error ? { color: "error.main" } : undefined}>
                      {t("bulkUpload.help.example.dateFormat")}
                    </Typography>
                  }
                  isInvalid={!!error}
                  isDayDisabled={isDateInvalidForDelivery}
                  startAdornment={error && <ErrorOutlineIcon fontSize="small" sx={{ color: "error.main" }} />}
                />
              )}
              name="endDate"
              control={control}
              rules={{
                required: true,
                validate: checkInvalidDates,
              }}
            />

            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Box sx={{ mb: "0 !important" }}>
                  <QuantityInput
                    value={value}
                    helperLabel={false}
                    onValueChange={(newValue) => {
                      onChange(newValue);
                      if (selectedProduct) {
                        trigger("packagingOption");
                      }
                    }}
                    step={1}
                    min={1}
                    startAdornment={error && <ErrorOutlineIcon sx={{ color: "error.main", pr: 1, fontSize: 16 }} />}
                    label={t("contractDetails.totals.totalQty") + "*"}
                    error={!!error}
                  />
                </Box>
              )}
              name="totalQuantity"
              control={control}
              rules={{ required: true, validate: checkInvalidQuantity }}
            />
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Box sx={{ mb: "0 !important" }}>
                  <QuantityInput
                    value={value}
                    helperLabel={false}
                    onValueChange={onChange}
                    step={1}
                    min={1}
                    startAdornment={error && <ErrorOutlineIcon sx={{ color: "error.main", pr: 1, fontSize: 16 }} />}
                    label={t("newContract.numberOfDeliveries") + "*"}
                    error={!!error}
                  />
                </Box>
              )}
              name="numberOfDeliveries"
              control={control}
              rules={{ required: true }}
            />
          </Tile>
          <Tile title={t("orderDetailsPage.table.header.product")}>
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <AutosuggestProductInput
                  {...inputProps}
                  label={t("bulkUpload.table.header.productId") + "*"}
                  inputValue={value ?? ""}
                  onInputChange={(event, selectedValue) => {
                    onChange(selectedValue);
                  }}
                  products={products}
                  error={!!error}
                  startAdornment={error && <ErrorOutlineIcon sx={{ color: "error.main", pr: 1, fontSize: 16 }} />}
                  inputProps={{
                    "clta-new-contract-page-element-marker": "product-id-input",
                  }}
                />
              )}
              name="productId"
              control={control}
              rules={{ required: true }}
            />

            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <SimpleSelect
                  value={value}
                  sx={{ "&:before": { borderBottomStyle: "solid !important" } }}
                  onChange={(chosenSize) => {
                    onChange(chosenSize);
                    if (selectedProduct) {
                      trigger("totalQuantity");
                    }
                  }}
                  fullWidth
                  InputProps={{
                    startAdornment: error && <ErrorOutlineIcon sx={{ color: "error.main", pr: 1, fontSize: 16 }} />,
                  }}
                  label={t("addToCart.contract.packaging") + "*"}
                  values={
                    packageOptions?.packagingInformation.map((entry) => ({
                      label: t("addToCartPopup.packSize", {
                        amount: entry.packSize,
                        code: entry.packCode,
                      }),
                      value: entry.packSize,
                    })) ?? []
                  }
                  error={!!error}
                  disabled={!packageOptions?.packagingInformation}
                />
              )}
              name="packagingOption"
              control={control}
              rules={{
                required: true,
                validate: checkInvalidQuantity,
              }}
            />
          </Tile>
          <Tile title={t("newContract.shippingTile")}>
            <Stack gap={0.5}>
              <Stack direction="row" justifyContent="space-between">
                <span>{t("checkoutPage.headers.shippingAddress")}</span>
                <ActionIconButton
                  onClick={showAddressModal}
                  clickableType={ActionIconButtonType.PRIMARY}
                  icon={<EditIcon />}
                />
              </Stack>
              <Stack sx={{ typography: "labelLarge" }}>
                <span className="f-bold">{watch("shippingCompany")}</span>
                <span>{`${watch("shippingAddressLine1") ?? ""} ${watch("shippingAddressLine2") ?? ""}`}</span>
                <span>{`${watch("shippingAddressPostalCode") ?? ""} ${watch("shippingAddressProvince") ?? ""}`}</span>
                <span>
                  <Trans i18nKey={watch("shippingAddressCountry")} />
                </span>
              </Stack>
            </Stack>
            {filteredUnloadingPoints && filteredUnloadingPoints?.length > 0 && (
              <Controller
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <SimpleSelect
                    value={value}
                    onChange={onChange}
                    fullWidth
                    label={
                      <Stack direction="row" gap={1}>
                        {error && <ErrorOutlineIcon />}
                        <span>{t("checkoutPage.headers.unloading") + "*"}</span>
                      </Stack>
                    }
                    values={filteredUnloadingPoints.map((point) => ({ label: point.name, value: point.name })) ?? []}
                    error={!!error}
                  />
                )}
                name="unloadingPoint"
                control={control}
                rules={{ required: filteredUnloadingPoints?.length > 0 ? true : false }}
              />
            )}
          </Tile>
          <Stack justifyContent="space-between">
            <Controller
              render={({ field: { value, onChange } }) => (
                <TextField
                  variant={"outlined"}
                  multiline
                  fullWidth
                  inputProps={{ sx: { height: "170px !important" } }}
                  value={value}
                  onChange={onChange}
                  minRows={"9"}
                  placeholder={t("newContract.commentPlaceholder")}
                  hidden={false}
                />
              )}
              name="comment"
              control={control}
            />

            <DynamicErrorMessage errors={formState.errors} />
          </Stack>
        </TilesGrid>
        <div>{`*${t("checkoutPage.addressModal.mandatory")}`}</div>
        <ActionsGrid>
          <Button
            onClick={() => reset(EMPTY_VALUES)}
            startIcon={<RestartAltIcon />}
            variant="text"
            sx={{ gridArea: "clear" }}
          >
            {t("searchPage.refinement.button.clearAll")}
          </Button>
          <Button
            type="submit"
            sx={{ gridArea: "submit" }}
            clta-new-contract-page-element-marker="request-new-contract-button"
          >
            {t("newContract.submit")}
          </Button>
        </ActionsGrid>
      </Stack>
    </form>
  );
};

export interface Props {
  addresses: AddressDTO[];
  soldTo: B2BUnitDataDTO;
}

export function createRequestContractPage(data: Props) {
  return <RequestContractPage {...data} />;
}
